import { inject, Injectable } from '@angular/core';
import { ApiService } from '@services/api.service';
import { Observable } from 'rxjs';
import {
  ClientWithPermissionsApiResponse,
  SaveClientType,
  ClientConfigOptionsResponse,
  TenantConfiguration,
  UserTenantsApiResponse,
} from '@shared/types/clients.types';
import { HttpParams } from '@angular/common/http';
import { PaginationParams } from '@shared/types/data-table.types';

@Injectable({
  providedIn: 'root',
})
export class ClientsApiService {
  private apiService = inject(ApiService);

  /**
   * Retrieves the organization configuration needed by the client settings UI.
   * @param organizationId - Organization identifier used for the request path.
   * @returns Observable emitting the organization configuration options.
   */
  getOrgConfig({
    organizationId,
  }: {
    organizationId: string;
  }): Observable<ClientConfigOptionsResponse> {
    let params = new HttpParams();
    return this.apiService.get(
      `organizations/${organizationId}/all-configurations`,
      params,
    );
  }

  /**
   * Retrieves the list of tenants available for a given organization in the user-client flow.
   * @param options - Optional organizationId (when omitted, request path will be constructed with `undefined`).
   * @returns Observable emitting the user tenants API response.
   */
  getUserClientsList(options: {
    organizationId?: string | null;
  }): Observable<UserTenantsApiResponse> {
    let params = new HttpParams();
    return this.apiService.get(
      `user/${options.organizationId}/tenants`,
      params,
    );
  }

  /**
   * Retrieves the paginated list of clients/tenants for an organization, including permissions.
   * @param options - OrganizationId and pagination parameters.
   * @returns Observable emitting the clients list API response.
   */
  getClientsList(options: {
    organizationId?: string | null;
    paginationParams: PaginationParams;
  }): Observable<ClientWithPermissionsApiResponse> {
    let params = new HttpParams()
      .set('page', options.paginationParams.page.toString())
      .set('pageSize', options.paginationParams.pageSize.toString())
      .set('sortOrder', options.paginationParams.sortOrder);
    if (options.paginationParams.sortBy) {
      params = params.set('sortBy', options.paginationParams.sortBy);
    }

    return this.apiService.get(
      `organizations/${options.organizationId}/tenants`,
      params,
    );
  }

  /**
   * Creates a new client/tenant for an organization.
   * @param options - Optional organizationId and the client payload to persist.
   * @returns Observable emitting the created resource response.
   */
  createNewClient(options: {
    organizationId?: string | null;
    client: SaveClientType;
  }): Observable<any> {
    let params = new HttpParams();

    return this.apiService.post(
      `organizations/${options.organizationId}/tenants`,
      { ...options.client },
      params,
    );
  }

  /**
   * Updates an existing client/tenant.
   * The request body excludes `tenantID` because the server path already identifies the tenant.
   * @param client - Client payload containing id, tenantID, and updated config.
   * @returns Observable emitting the update response.
   */
  updateClient(client: SaveClientType): Observable<any> {
    let params = new HttpParams();
    const { tenantID, ...newClient } = client;
    return this.apiService.put(`tenants/${tenantID}`, newClient, params);
  }

  /**
   * Deletes a client/tenant by id.
   * @param id - Tenant id to delete.
   * @returns Observable emitting the delete response.
   */
  deleteClient(id: string): Observable<any> {
    let params = new HttpParams();
    return this.apiService.delete(`tenants/${id}`, params);
  }

  /**
   * Get tenant configuration including organization info and permissions
   * @param tenantId - The ID of the tenant to get configuration for
   * @returns Observable with tenant configuration containing permissions array
   */
  getTenantConfiguration(tenantId: string): Observable<TenantConfiguration> {
    let params = new HttpParams();
    return this.apiService.get<TenantConfiguration>(
      `tenants/${tenantId}/configuration`,
      params,
    );
  }
}
